import { memo, useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts";
import { Card, CardBody, Col, Row } from "reactstrap";

const CHART_TITLES = {
	q5: "Bagian Aplikasi Paling Sering Digunakan",
	q6: "Pendapat Tampilan Aplikasi",
	q7: "Kemungkinan Rekomendasi",
};

const CHART_COLORS = ["#1E40AF", "#3B82F6", "#60A5FA", "#D97706", "#F59E0B", "#94A3B8"];

const ChartCard = ({ title, children }) => (
	<Card className="border-0 rounded-4 h-100" style={{ boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)" }}>
		<CardBody className="p-3">
			<h6 className="fw-semibold mb-3">{title}</h6>
			{children}
		</CardBody>
	</Card>
);

const ReadOnlyChart = ({ option, height = 320 }) => {
	const chartRef = useRef(null);
	const chartInstance = useRef(null);

	useEffect(() => {
		const dom = chartRef.current;
		if (!dom) return;

		chartInstance.current = echarts.init(dom);

		const handleResize = () => chartInstance.current?.resize();
		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
			chartInstance.current?.dispose();
			chartInstance.current = null;
		};
	}, []);

	useEffect(() => {
		if (!chartInstance.current) return;
		chartInstance.current.setOption(option, true);
	}, [option]);

	return <div ref={chartRef} style={{ width: "100%", height }} />;
};

const buildPieOption = (items, donut = false) => {
	const seriesData = items.map((item, index) => ({
		name: item.label,
		value: item.count,
		itemStyle: { color: CHART_COLORS[index % CHART_COLORS.length] },
	}));

	const hasData = seriesData.some((item) => item.value > 0);

	return {
		color: CHART_COLORS,
		tooltip: { trigger: "item", formatter: "{b}: {c} ({d}%)" },
		legend: {
			type: "scroll",
			orient: "horizontal",
			bottom: 0,
			textStyle: { fontSize: 12 },
		},
		series: [
			{
				type: "pie",
				radius: donut ? ["40%", "62%"] : "58%",
				center: ["50%", "42%"],
				data: hasData ? seriesData : [{ name: "Belum ada data", value: 1, itemStyle: { color: "#E2E8F0" } }],
				label: {
					show: hasData,
					position: "inside",
					formatter: "{d}%",
					fontSize: 11,
					color: "#fff",
					fontWeight: 600,
				},
				labelLine: { show: false },
				minShowLabelAngle: 12,
				emphasis: {
					label: { show: true, fontSize: 12, fontWeight: "bold" },
					itemStyle: {
						shadowBlur: 8,
						shadowOffsetX: 0,
						shadowColor: "rgba(30, 64, 175, 0.15)",
					},
				},
			},
		],
	};
};

const buildBarOption = (items) => {
	const categories = items.map((item) => item.label);
	const values = items.map((item) => item.count);
	const hasData = values.some((value) => value > 0);

	return {
		color: CHART_COLORS,
		tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
		grid: { left: "4%", right: "4%", bottom: "8%", containLabel: true },
		xAxis: {
			type: "category",
			data: categories,
			axisLabel: { interval: 0, fontSize: 12 },
		},
		yAxis: {
			type: "value",
			minInterval: 1,
		},
		series: [
			{
				type: "bar",
				data: hasData ? values : categories.map(() => 0),
				itemStyle: { borderRadius: [6, 6, 0, 0], color: "#3B82F6" },
				label: {
					show: true,
					position: "top",
					formatter: ({ value }) => (value > 0 ? value : ""),
				},
			},
		],
	};
};

const SurveyCharts = ({ analytics }) => {
	const charts = analytics?.charts;

	const q5Option = useMemo(
		() => (charts?.q5 ? buildPieOption(charts.q5) : null),
		[charts?.q5]
	);
	const q6Option = useMemo(
		() => (charts?.q6 ? buildPieOption(charts.q6, true) : null),
		[charts?.q6]
	);
	const q7Option = useMemo(
		() => (charts?.q7 ? buildBarOption(charts.q7) : null),
		[charts?.q7]
	);

	if (!charts || !q5Option || !q6Option || !q7Option) return null;

	return (
		<Row className="g-3 mb-4">
			<Col lg="4" md="6" xs="12">
				<ChartCard title={CHART_TITLES.q5}>
					<ReadOnlyChart option={q5Option} height={360} />
				</ChartCard>
			</Col>
			<Col lg="4" md="6" xs="12">
				<ChartCard title={CHART_TITLES.q6}>
					<ReadOnlyChart option={q6Option} height={360} />
				</ChartCard>
			</Col>
			<Col lg="4" md="12" xs="12">
				<ChartCard title={CHART_TITLES.q7}>
					<ReadOnlyChart option={q7Option} height={340} />
				</ChartCard>
			</Col>
		</Row>
	);
};

export default memo(SurveyCharts);
